import { getRandomElement, getRandomInt, assertDefined } from '@utils';
import loremJson from './lorem.json';

const loremData = loremJson as Record<string, unknown>;
const loremHelloData = loremData.helloWorld as Record<string, string>;
const loremExampleData = loremData.exampleText as Record<string, string>;
const loremArray = loremJson as unknown as Record<string, string[]>;

export type LoremLocale =
  | 'en'
  | 'es'
  | 'de'
  | 'fr'
  | 'ru'
  | 'hi'
  | 'ko'
  | 'th'
  | 'ja'
  | 'zn'
  | 'he'
  | 'ar'
  | 'em';

export const loremLocales: LoremLocale[] = [
  'en',
  'es',
  'de',
  'fr',
  'ru',
  'hi',
  'ko',
  'th',
  'ja',
  'zn',
  'he',
  'ar',
  'em'
];

export function loremify(s: string): string {
  const components = s.split(' ');
  switch (components[0]) {
    case '__': {
      let wordCount = parseInt(components[1]);
      if (Number.isNaN(wordCount) || wordCount < 1 || wordCount > 100) {
        wordCount = 5;
      }
      const locale = components[2] as LoremLocale;
      return lorem(wordCount, locale);
    }
    case '__h': {
      const locale = components[1] as LoremLocale;
      return loremHello(locale);
    }
    case '__x': {
      const locale = components[1] as LoremLocale;
      return loremExample(locale);
    }
    default:
      return s;
  }
}

function normalizeLocale(locale?: string): LoremLocale {
  if (loremLocales.includes(locale as LoremLocale)) {
    return locale as LoremLocale;
  } else {
    return 'en';
  }
}

export function loremHello(locale?: LoremLocale): string {
  return loremHelloData[normalizeLocale(locale)];
}

export function loremExample(locale?: LoremLocale): string {
  return loremExampleData[normalizeLocale(locale)];
}

export function lorem(
  wordCount: number,
  locale: LoremLocale | 'mixed' = 'en',
  titleCase = false
): string {
  let normalizedLocale = normalizeLocale(locale);

  const useSpaces = !['zn', 'ja'].includes(normalizedLocale);

  const isMixed = locale === 'mixed';

  let result = '';
  for (let i = 0; i < wordCount; i++) {
    if (isMixed) {
      const loc = getRandomElement(loremLocales);
      assertDefined(loc);
      normalizedLocale = loc;
    }
    const vocabulary = loremArray[normalizedLocale];
    const randomIndex = getRandomInt(0, vocabulary.length - 1);
    const word = vocabulary[randomIndex];
    if (titleCase) {
      result += capitalize(word, locale);
    } else {
      result += word;
    }
    if (useSpaces && i < wordCount - 1) {
      result += ' ';
    }
  }

  return result;
}

function capitalize(str: string, locale: string): string {
  return str
    .split(' ')
    .map(word => word.charAt(0).toLocaleUpperCase(locale) + word.slice(1))
    .join(' ');
}
